import type { AssociatedObject } from "./types";

type UnknownRecord = Record<string, unknown>;

function asRecord(value: unknown): UnknownRecord | null {
  if (value && typeof value === "object" && !Array.isArray(value)) return value as UnknownRecord;
  return null;
}

function asNonEmptyString(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

function asNumber(value: unknown): number | undefined {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  return undefined;
}

function asStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value
    .map(asNonEmptyString)
    .filter((entry): entry is string => typeof entry === "string");
}

function uniqueStrings(values: string[]): string[] {
  const seen = new Set<string>();
  return values
    .map((value) => value.trim())
    .filter((value) => value.length > 0)
    .filter((value) => {
      if (seen.has(value)) return false;
      seen.add(value);
      return true;
    });
}

export type MeetingViewModel = {
  title: string;
  organizer?: string;
  participants: string[];
  startTime?: string;
  endTime?: string;
  durationSeconds?: number;
  meetingUrl?: string;
  source?: string;
  // Recording fields (populated when the object is a meeting_recording or a meeting with embedded recording data)
  recordingUrl?: string;
  provider?: string;
  language?: string;
  summaryMd?: string;
  transcriptText?: string;
  actionItems: string[];
};

export type MeetingRecordLike = {
  meta_data: Record<string, unknown>;
  created_at?: string | null;
  key_fields_text?: string | null;
};

function safeParseJsonArray(value: string): unknown[] {
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function getMeetingViewModel(
  meeting: Pick<AssociatedObject, "meta_data" | "created_at" | "key_fields_text"> | MeetingRecordLike,
): MeetingViewModel {
  const metadata = asRecord(meeting.meta_data) ?? {};

  const title =
    asNonEmptyString(metadata.title) ??
    asNonEmptyString(metadata.native_title) ??
    "(No title)";

  const organizer =
    asNonEmptyString(metadata.organizer) ??
    asNonEmptyString(metadata.native_organizer_id);

  const startTime =
    asNonEmptyString(metadata.start_time) ??
    asNonEmptyString(metadata.native_start_time_tz);

  const endTime =
    asNonEmptyString(metadata.end_time) ??
    asNonEmptyString(metadata.native_end_time_tz);

  const durationSeconds =
    asNumber(metadata.native_duration_seconds) ??
    asNumber(metadata.duration_seconds);

  const meetingUrl =
    asNonEmptyString(metadata.meeting_url) ??
    asNonEmptyString(metadata.native_meeting_url);

  const participants = uniqueStrings([
    ...asStringArray(metadata.attendees),
    ...asStringArray(metadata.native_participants_emails),
    ...asStringArray(metadata.native_access_recipients),
  ]);

  const source =
    asNonEmptyString(metadata.native_source) ??
    asNonEmptyString(metadata.source);

  // Recording fields
  const recordingUrl =
    asNonEmptyString(metadata.recording_url) ??
    asNonEmptyString(metadata.native_recording_url);

  const provider =
    asNonEmptyString(metadata.provider) ??
    asNonEmptyString(metadata.native_provider);

  const language =
    asNonEmptyString(metadata.language) ??
    asNonEmptyString(metadata.native_language);

  const summaryMd =
    asNonEmptyString(metadata.summary_md) ??
    asNonEmptyString(metadata.native_summary_md);

  const transcriptText =
    asNonEmptyString(metadata.transcript_text) ??
    asNonEmptyString(metadata.native_transcript_text);

  const rawActionItems = metadata.action_items ?? metadata.native_action_items;
  const actionItems = asStringArray(
    typeof rawActionItems === "string" ? safeParseJsonArray(rawActionItems) : rawActionItems,
  );

  return {
    title,
    organizer,
    participants,
    startTime,
    endTime,
    durationSeconds,
    meetingUrl,
    source,
    recordingUrl,
    provider,
    language,
    summaryMd,
    transcriptText,
    actionItems,
  };
}

export function meetingMatchesQuery(
  meeting: Pick<AssociatedObject, "meta_data" | "created_at" | "key_fields_text">,
  query: string,
): boolean {
  const trimmed = query.trim();
  if (!trimmed) return true;

  const view = getMeetingViewModel(meeting);
  const haystack = [
    view.title,
    view.organizer ?? "",
    view.participants.join(" "),
    view.source ?? "",
    meeting.key_fields_text ?? "",
  ]
    .join(" ")
    .toLowerCase();

  return haystack.includes(trimmed.toLowerCase());
}

export function safeDate(value?: string): Date | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date;
}
